import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Form,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
  Pagination,
} from "react-bootstrap";
import moment from "moment";
import axiosClient from "../api/axiosClient";
import { API_BASE_URL } from "../config/api";
import JSONPretty from "react-json-pretty";
import "react-json-pretty/themes/monikai.css";

const RequestLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [startDate, setStartDate] = useState(
    moment().subtract(7, "days").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [filters, setFilters] = useState({
    userId: "",
    endpoint: "",
    method: "",
    statusCode: "",
    search: "",
    limit: 50,
    offset: 0,
    errorOnly: "false",
  });
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
  });
  const [expandedRow, setExpandedRow] = useState(null);

  // Fetch logs data with filters
  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError("");

      const queryParams = new URLSearchParams({
        ...filters,
        startDate,
        endDate,
      });

      const response = await axiosClient.get(
        `https://api.viralgpt.com/admin/request-logs?${queryParams}`,
      );

      setLogs(response.data.logs);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error("Error fetching logs data:", error);
      setError("Failed to load logs data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchLogs();
  }, [startDate, endDate, filters.offset, filters.limit]);

  // Handle filter changes
  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      offset: 0, // Reset pagination when filters change
    }));
  };

  // Apply filters
  const applyFilters = () => {
    fetchLogs();
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      userId: "",
      endpoint: "",
      method: "",
      statusCode: "",
      search: "",
      limit: 50,
      offset: 0,
      errorOnly: "false",
    });
    setStartDate(moment().subtract(7, "days").format("YYYY-MM-DD"));
    setEndDate(moment().format("YYYY-MM-DD"));
  };

  // Toggle error-only filter
  const toggleErrorOnly = () => {
    setFilters((prev) => ({
      ...prev,
      errorOnly: prev.errorOnly === "true" ? "false" : "true",
      offset: 0, // Reset pagination
    }));
  };

  // Handle pagination
  const handlePageChange = (newOffset) => {
    setFilters((prev) => ({
      ...prev,
      offset: newOffset,
    }));
  };

  // Toggle row expansion
  const toggleRowExpansion = (id) => {
    if (expandedRow === id) {
      setExpandedRow(null);
    } else {
      setExpandedRow(id);
    }
  };

  // Format timestamp - backend sends CST but incorrectly marks it with Z
  const formatTimestamp = (timestamp) => {
    // Remove the incorrect Z suffix and parse as local time
    const cleanTimestamp = timestamp.replace('Z', '');
    return moment(cleanTimestamp).format("MMMM D, YYYY h:mm:ss A");
  };

  // Render pagination controls
  const renderPagination = () => {
    const totalPages = Math.ceil(pagination.total / pagination.limit);
    const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

    const items = [];

    // Previous button
    items.push(
      <Pagination.Prev
        key="prev"
        disabled={currentPage === 1}
        onClick={() => handlePageChange((currentPage - 2) * pagination.limit)}
      />,
    );

    // Page numbers
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + 4);

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange((page - 1) * pagination.limit)}
        >
          {page}
        </Pagination.Item>,
      );
    }

    // Next button
    items.push(
      <Pagination.Next
        key="next"
        disabled={currentPage === totalPages}
        onClick={() => handlePageChange(currentPage * pagination.limit)}
      />,
    );

    return (
      <Pagination className="justify-content-center mt-3">{items}</Pagination>
    );
  };

  const TruncatedCell = ({ content, maxLength = 25 }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const isTruncated = content.length > maxLength;

    const displayText = isExpanded
      ? content
      : `${content.slice(0, maxLength)}${isTruncated ? "..." : ""}`;

    return (
      <div
        style={{ cursor: isTruncated ? "pointer" : "default" }}
        onDoubleClick={() => isTruncated && setIsExpanded(!isExpanded)}
        title={isTruncated ? "Double-click to expand" : ""}
      >
        {displayText}
      </div>
    );
  };

  return (
    <div style={{background:'#fff',borderRadius:'16px',border:'1px solid #e3e8ee',boxShadow:'0 2px 4px rgba(50,50,93,0.06)',marginBottom:'1.5rem'}}>
      <div style={{padding:0}}>
        <Card.Title className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4 p-3">
          <span style={{fontWeight:700,color:'#0a2540',fontSize:'1.1rem'}}>Request Logs</span>
          <div className="d-flex gap-2 mt-2 mt-sm-0 w-100 w-sm-auto">
            <Form.Group as={Row} className="align-items-center w-100">
              <Form.Label column sm="auto">
                Date Range:
              </Form.Label>
              <Col sm="auto">
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Col>
              <Col sm="auto">to</Col>
              <Col sm="auto">
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Col>
            </Form.Group>
          </div>
        </Card.Title>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {/* Filters */}
        <div className="p-3 border-bottom">
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Search Payloads</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search request & response payloads..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  style={{ borderRadius: 8, border: '1px solid #e3e8ee', boxShadow: '0 1px 2px rgba(50,50,93,0.08)' }}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>User ID</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Filter by user ID"
                  value={filters.userId}
                  onChange={(e) => handleFilterChange("userId", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Endpoint</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Filter by endpoint"
                  value={filters.endpoint}
                  onChange={(e) =>
                    handleFilterChange("endpoint", e.target.value)
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Method</Form.Label>
                <Form.Select
                  value={filters.method}
                  onChange={(e) => handleFilterChange("method", e.target.value)}
                >
                  <option value="">All Methods</option>
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Status Code</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Filter by status code"
                  value={filters.statusCode}
                  onChange={(e) =>
                    handleFilterChange("statusCode", e.target.value)
                  }
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Results per page</Form.Label>
                <Form.Select
                  value={filters.limit}
                  onChange={(e) => handleFilterChange("limit", e.target.value)}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mt-md-4">
                <Form.Check
                  type="switch"
                  id="error-only-switch"
                  label="Show only error codes (400-599)"
                  checked={filters.errorOnly === "true"}
                  onChange={toggleErrorOnly}
                />
              </Form.Group>
            </Col>
            <Col md={6} className="d-flex align-items-end justify-content-end">
              <Button
                variant="secondary"
                className="me-2"
                onClick={resetFilters}
              >
                Reset Filters
              </Button>
              <button onClick={applyFilters} style={{background:'linear-gradient(135deg,#635bff 0%,#7c3aed 100%)',color:'#fff',border:'none',borderRadius:'10px',fontWeight:600,fontSize:'0.85rem',padding:'0.5rem 1rem',cursor:'pointer',boxShadow:'0 4px 6px rgba(50,50,93,0.11), 0 1px 3px rgba(0,0,0,0.08)'}}>
                Apply Filters
              </button>
            </Col>
          </Row>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <div className="table-responsive">
            <Table striped bordered hover className="logs-table mb-0">
              <thead className="bg-light">
                <tr>
                  <th style={{ width: "8%" }}>User ID</th>
                  <th style={{ width: "12%" }}>Timestamp</th>
                  <th style={{ width: "40%" }}>Endpoint</th>
                  <th style={{ width: "10%" }}>Method</th>
                  <th style={{ width: "10%" }}>Status</th>
                  <th style={{ width: "20%" }}>JSON Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center">
                      No logs found matching the criteria
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <React.Fragment key={log.id}>
                      <tr>
                        <td>{log.userid || "N/A"}</td>
                        <td>{formatTimestamp(log.timestamp)}</td>
                        <td>
                          <TruncatedCell content={log.endpoint} />
                        </td>
                        <td>{log.method}</td>
                        <td>
                          <span
                            className={`badge ${log.status_code >= 400 ? "bg-danger" : "bg-success"}`}
                          >
                            {log.status_code}
                          </span>
                        </td>
                        <td>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => toggleRowExpansion(log.id)}
                          >
                            {expandedRow === log.id ? "Hide" : "Show"}
                          </Button>
                        </td>
                      </tr>
                      {expandedRow === log.id && (
                        <tr>
                          <td colSpan="9" className="p-3 bg-light">
                            <Row>
                              <Col md={6}>
                                <h6>Request Payload</h6>
                                <div
                                  className="border rounded p-2 bg-dark text-light overflow-auto"
                                  style={{
                                    maxHeight: "300px",
                                    maxWidth: "100%",
                                  }}
                                >
                                  <JSONPretty
                                    id={`request-${log.id}`}
                                    data={log.request_payload || {}}
                                    mainStyle="padding:0.5rem;overflow-x:auto;white-space:pre-wrap;word-break:break-word;"
                                  />
                                </div>
                              </Col>
                              <Col md={6}>
                                <h6>Response Payload</h6>
                                <div
                                  className="border rounded p-2 bg-dark text-light overflow-auto"
                                  style={{
                                    maxHeight: "300px",
                                    maxWidth: "100%",
                                  }}
                                >
                                  <JSONPretty
                                    id={`response-${log.id}`}
                                    data={log.response_payload || {}}
                                    mainStyle="padding:0.5rem;overflow-x:auto;white-space:pre-wrap;word-break:break-word;"
                                  />
                                </div>
                              </Col>
                            </Row>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </Table>
            {renderPagination()}
          </div>
        )}
      </div>

      {/* Add mobile-friendly styles similar to UserJourney.js */}
      <style jsx="true">{`
        /* Default (non-mobile) table styling */
        .logs-table {
          font-size: 1rem;
          table-layout: fixed;
          width: 100%;
        }

        /* Prevent JSON content from breaking the layout */
        .logs-table pre {
          white-space: pre-wrap;
          word-break: break-word;
          max-width: 100%;
          overflow-x: auto;
        }

        /* Mobile adjustments */
        @media (max-width: 576px) {
          .logs-table {
            table-layout: fixed;
            width: 100%;
            font-size: 0.4rem;
          }
          .logs-table th,
          .logs-table td {
            padding: 0;
            text-align: center;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: clip;
          }
          .logs-table .badge {
            font-size: 0.4rem;
            padding: 0.2em 0.4em;
          }
          .logs-table button {
            font-size: 0.4rem;
            padding: 0.1rem 0.3rem;
          }
        }
      `}</style>
    </div>
  );
};

export default RequestLogs;
